package com.xtream.ui.ui.widget

import androidx.compose.animation.animateColorAsState
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.width
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.NavigationDrawerItemColors
import androidx.compose.material3.NavigationDrawerItemDefaults
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.role
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp

@Composable
fun CustomNavigationDrawerItem(
    label: @Composable () -> Unit,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    icon: (@Composable () -> Unit)? = null,
    badge: (@Composable () -> Unit)? = null,
    shape: Shape = MaterialTheme.shapes.extraSmall,
    colors: NavigationDrawerItemColors = NavigationDrawerItemDefaults.colors(),
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() }
) {
    // 1. Monitor focus state to mimic TV leanback surface changes
    val isFocused by interactionSource.collectIsFocusedAsState()

    // 2. Animate container color based on focus & selection states
    val animatedContainerColor by animateColorAsState(
        targetValue = when {
            isFocused -> colors.containerColor(selected = true).value // Use active color on TV focus
            selected -> colors.containerColor(selected = true).value
            else -> Color.Transparent // Mimics your transparent surface defaults
        },
        label = "DrawerItemContainerColor"
    )

    Surface(
        modifier = modifier
            .semantics { role = Role.Tab }
            .heightIn(min = 30.dp, max = 30.dp)
            .fillMaxWidth()
            .clickable(
                interactionSource = interactionSource,
                onClick = onClick,
                indication = null // Disables standard mobile ripple effect for a clean TV UI
            ),
        shape = shape,
        color = animatedContainerColor,
        contentColor = when {
            isFocused -> colors.textColor(selected = true).value
            selected -> colors.textColor(selected = true).value
            else -> colors.textColor(selected = false).value
        }
    ) {
        Row(
           // modifier = Modifier.padding(horizontal = 16.dp), // Added balanced padding for television screens
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (icon != null) {
                // Determine icon coloring dynamically based on runtime interaction state
                val iconColor = when {
                    isFocused || selected -> colors.iconColor(selected = true).value
                    else -> colors.iconColor(selected = false).value
                }
                CompositionLocalProvider(LocalContentColor provides iconColor) {
                    Box(content = { icon() })
                }
                Spacer(Modifier.width(32.dp)) // Kept your exact 32.dp spec
            }

            Box(Modifier.weight(1f)) {
                val labelColor = when {
                    isFocused || selected -> colors.textColor(selected = true).value
                    else -> colors.textColor(selected = false).value
                }
                CompositionLocalProvider(LocalContentColor provides labelColor) {
                    Box(content = { label() })
                }
            }

            if (badge != null) {
                Spacer(Modifier.width(12.dp)) // Kept your exact 12.dp spec
                val badgeColor = when {
                    isFocused || selected -> colors.badgeColor(selected = true).value
                    else -> colors.badgeColor(selected = false).value
                }
                CompositionLocalProvider(LocalContentColor provides badgeColor) {
                    Box(content = { badge() })
                }
            }
        }
    }
}